<template>
  <div class="rx-page">
    <header class="rx-topbar">
      <div class="rx-topbar__patient">
        <span class="avatar avatar--sm">{{ patientInitials }}</span>
        <span class="rx-topbar__name">{{ patientName }}</span>
      </div>
    </header>

    <div v-if="hasFailed" class="rx-loader">
      <div class="rx-loader-msg">
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="rgba(220,38,38,.7)"
            stroke-width="2"
          />
          <path
            d="M12 8v4M12 16h.01"
            stroke="#fff"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
        <p class="rx-loader-title">{{ failureMessage }}</p>
        <div class="rx-loader-actions">
          <!-- Recarregar só ajuda quando a falha é de carregamento. Para o
               médico que a Memed não aprova, mandar apertar F5 é instrução
               errada — ele ficaria recarregando sem sair do lugar. -->
          <p v-if="!isBlocked" class="rx-loader-hint">
            Por favor, atualize-a (F5 ou cmd+R). Caso o erro persista, feche
            esta aba e clique no botão de Prescrição novamente.
          </p>

          <!-- <button class="btn btn--outline" @click="init">
            Tentar novamente
          </button> -->
          <button class="btn btn--outline" @click="closeTab">
            Fechar aba
          </button>
        </div>

        <!-- O médico não tem como saber qual das causas o derrubou, e nós
             tampouco tínhamos até agora. Este código é o mesmo que vai na tag
             `memed.code` do Sentry: ditado ao suporte, identifica a falha sem
             depender de o evento ter sido encontrado no painel. -->
        <p v-if="errorCode" class="rx-loader-code">
          Código do erro: {{ errorCode }}
        </p>
      </div>
    </div>

    <div v-else class="rx-content">
      <!-- Fica por cima do #memed-container enquanto os dados ainda estão
           sendo buscados OU enquanto o hub Memed já recebeu o script mas
           ainda está montando o módulo por dentro (ver core:onready em
           openPrescription/showPrescriptionModule) — sem isso a tela fica
           em branco nesse meio-tempo. -->
      <div
        v-if="isLoading || !isModuleReady"
        class="rx-loader rx-loader--overlay"
      >
        <div class="rx-loader-msg">
          <dot-loader />
          <p>Carregando prescrição médica…</p>
        </div>
      </div>

      <div v-if="!isLoading" id="memed-container" class="rx-body" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  ensureMemedSdk,
  isMemedModuleVisible,
  memedMessageFor,
  MEMED_BLOCKED_MESSAGE,
  MEMED_UNAVAILABLE_MESSAGE
} from "../utils/memed";
import { resolveMemedToken } from "../utils/memedToken";
import { reportMemedFailure } from "../utils/memedDiagnostics";
import { takeDiagnosticToken } from "../utils/prescriptionDiagnostic";
import dePara from "@/utils/drugs";
import { formatFromENtoPT } from "@/utils/dates/format";
import DotLoader from "../components/DotLoader.vue";

const LOAD_ERROR_MESSAGE = "Houve um problema ao tentar carregar a página.";

// Teto de reenvios de comando por rajada de `core:onready`. Não é o sinal de
// desistência — é o que impede a reentrância de saturar a main thread.
const MAX_SHOW_ATTEMPTS = 5;

// Sinal de desistência. Folgado de propósito: com cache frio o SDK leva
// segundos legitimamente, e cortar cedo daria falso negativo em rede lenta.
const MODULE_READY_TIMEOUT_MS = 20000;
const MODULE_READY_POLL_MS = 500;

export default {
  name: "PrescriptionView",
  components: {
    DotLoader
  },
  data() {
    return {
      isLoading: true,
      loadingError: null,
      errorCode: null,
      isBlocked: false,
      blockedMessage: MEMED_BLOCKED_MESSAGE,
      isModuleReady: false,
      room: null,
      memedPatient: null,
      channel: null,
      moduleObserver: null,
      hub: null,
      showAttempts: 0,
      readyPoll: null,
      readyDeadline: null,
      onReadyHandler: null,
      printedHandler: null,
      isDestroyed: false,
      diagnosticToken: null
    };
  },
  mounted() {
    // Lê o token de diagnóstico UMA vez, antes do init, e já o remove do
    // storage (feito dentro de takeDiagnosticToken). Só entra aqui quando a
    // aba foi aberta pelo modal de diagnóstico (`?diagnostic=1`).
    if (this.$route.query && this.$route.query.diagnostic) {
      const stash = takeDiagnosticToken();
      this.diagnosticToken = stash && stash.token ? stash.token : null;
    }
    this.init();
  },
  beforeDestroy() {
    // O MdHub é singleton da página e sobrevive ao componente: sem esta flag,
    // um handler registrado aqui continua rodando depois do destroy.
    this.isDestroyed = true;
    this.stopReadyWatch();
    this.removeHubListeners();
    if (this.channel) this.channel.close();
    if (this.moduleObserver) this.moduleObserver.disconnect();
  },
  methods: {
    ...mapActions("room", ["getRoomDetailsV2"]),
    ...mapActions("user", ["showSymptoms"]),
    ...mapActions("prescriptions", ["postPrescription"]),

    async init() {
      this.isLoading = true;
      this.loadingError = null;
      this.errorCode = null;
      this.isBlocked = false;
      this.isModuleReady = false;
      this.showAttempts = 0;

      // Três chamadas diferentes desembocavam no mesmo `catch`, e a mensagem
      // final era a mesma nas três. Guardar o estágio é o que separa "a API da
      // sala falhou" de "a Memed não respondeu" quando o relato chega.
      let stage = "room-fetch";

      try {
        const roomResponse = await this.getRoomDetailsV2(
          this.$route.params.room
        );
        this.room = roomResponse.data;

        stage = "patient-fetch";
        const symptomsResponse = await this.showSymptoms({
          id: this.room.patient.id
        });
        this.memedPatient = {
          ...symptomsResponse.data.user,
          id: this.room.patient.id
        };
        document.title = `Prescrição — ${this.patientName} · Videomedi`;

        stage = "token";
        // Modo diagnóstico: a aba foi aberta pelo modal "Testar prescrição"
        // com o token de OUTRO médico clonado no sessionStorage. Consome esse
        // token em vez do token da conta atual — todo o resto do fluxo (sala,
        // paciente, módulo Memed) é idêntico ao real. Fora do diagnóstico,
        // `diagnosticToken` é null e nada muda.
        const token =
          this.diagnosticToken ||
          (await resolveMemedToken(this.getCurrentUser));

        this.isLoading = false;
        await this.$nextTick();

        stage = "module-open";
        await this.loadPrescriptionModule(token);
      } catch (error) {
        this.isLoading = false;
        const cause = (error && error.message) || "";

        if (cause === "memed:not-approved") {
          // O backend viu a recusa na API da Memed: recarregar não resolve.
          this.blockedMessage = MEMED_BLOCKED_MESSAGE;
          this.isBlocked = true;
          this.errorCode = reportMemedFailure(
            stage,
            error,
            this.errorContext()
          );
        } else if (cause.startsWith("memed:")) {
          this.loadingError = MEMED_UNAVAILABLE_MESSAGE;
          this.errorCode = reportMemedFailure(
            stage,
            error,
            this.errorContext()
          );
        } else {
          this.loadingError = LOAD_ERROR_MESSAGE;
          this.errorCode = reportMemedFailure(
            stage,
            error,
            this.errorContext()
          );
        }
      }
    },

    // Só o que identifica a consulta — nada de dado clínico ou pessoal do
    // paciente vai para o Sentry.
    errorContext() {
      return {
        room_id: this.$route.params.room,
        appointment_id: this.room ? this.room.appointment_id : null,
        show_attempts: this.showAttempts
      };
    },

    // O SDK é carregado AQUI, e só aqui: ele instala iframes e um listener
    // global de `message` no documento, então não pode subir em telas que não
    // prescrevem.
    async loadPrescriptionModule(token) {
      try {
        this.hub = await ensureMemedSdk(token);
      } catch (error) {
        // Falha de carregamento (script bloqueado, rede, hub nunca publicado)
        // é diferente de médico recusado — recarregar a página ajuda aqui.
        this.loadingError = LOAD_ERROR_MESSAGE;
        this.errorCode = reportMemedFailure(
          "sdk-load",
          error,
          this.errorContext()
        );
        return;
      }
      if (this.isDestroyed) return;

      // `openPrescription` mexe em BroadcastChannel, no hub e no DOM sem rede
      // de proteção nenhuma: qualquer throw aqui subia até o `catch` de
      // `init()` e virava a mesma frase genérica, sem rastro de onde quebrou.
      try {
        this.openPrescription();
      } catch (error) {
        this.loadingError = LOAD_ERROR_MESSAGE;
        this.errorCode = reportMemedFailure(
          "module-open",
          error,
          this.errorContext()
        );
      }
    },

    // O canal só avisa a aba da sala que a receita saiu, para ela recarregar a
    // lista de anexos. É acessório: em navegador sem BroadcastChannel (Safari
    // < 15.4, e qualquer contexto em que o construtor lance) o `new` derrubava
    // a tela inteira antes mesmo de o módulo da Memed abrir. Prescrever é o
    // essencial; avisar a outra aba, não.
    openRoomChannel(name) {
      if (typeof BroadcastChannel === "undefined") {
        reportMemedFailure("broadcast-channel", null, this.errorContext());
        return null;
      }
      try {
        return new BroadcastChannel(name);
      } catch (error) {
        reportMemedFailure("broadcast-channel", error, this.errorContext());
        return null;
      }
    },

    openPrescription() {
      this.channel = this.openRoomChannel(
        `memed-prescription-${this.room.appointment_id}`
      );

      this.printedHandler = prescriptionData => {
        if (this.isDestroyed) return undefined;
        const data = {
          prescription: {
            appointment_id: this.room.appointment_id,
            lme: prescriptionData.prescricao.lme,
            memed_id: prescriptionData.prescricao.id,
            signed: prescriptionData.prescricao.signed,
            drugs_attributes: dePara(prescriptionData.prescricao.medicamentos)
          }
        };
        return this.postPrescription(data).then(() => {
          if (this.channel)
            this.channel.postMessage({ type: "prescription-created" });
        });
      };
      this.hub.event.add("prescricaoImpressa", this.printedHandler);

      // A Memed carrega todos os seus módulos (sdk, usuario,
      // patient-management, prescricao, digital-signature, ...) em paralelo
      // dentro de um #iframe-container que ela mantém com "display:none" até
      // terminar a própria inicialização. Cada módulo, ao terminar, dispara
      // "core:onready" (via MdHub.onReady, definido como
      // `MdHub.event.trigger("core:onready", MdHub.module.name)`) — o mesmo
      // evento que gera os logs "O módulo X está em execução" no console.
      // Quando o script acabou de ser injetado (diferente das outras telas,
      // que reaproveitam um MdHub carregado minutos antes no login), o
      // primeiro module.show() pode acontecer antes de tudo pronto e não ter
      // efeito — por isso reagimos a cada "core:onready" tentando de novo,
      // só enquanto o container ainda não tiver sido liberado.
      //
      // O freio: sem ele, o predicado "ainda não está visível" nunca deixa de
      // ser verdadeiro quando a Memed recusa o médico (401 em /v1/usuarios,
      // dentro dos iframes dela). Cada reenvio provocava novo bootstrap de
      // módulo, novo core:onready, e a aba travava. A condição de parada era
      // exatamente a condição que a falha impede.
      this.onReadyHandler = () => {
        if (this.isDestroyed || this.isModuleReady) return;
        if (this.isPrescriptionModuleVisible()) {
          this.markModuleReady();
          return;
        }
        if (this.showAttempts >= MAX_SHOW_ATTEMPTS) return;
        this.showPrescriptionModule();
      };
      this.hub.event.add("core:onready", this.onReadyHandler);

      this.startReadyWatch();
      this.showPrescriptionModule();
    },

    // Depois de esgotar as tentativas ainda esperamos passivamente: quem
    // decide "pronto" é a visibilidade real do container, e quem decide
    // "bloqueado" é o deadline — nunca o contador.
    startReadyWatch() {
      this.readyPoll = setInterval(() => {
        if (this.isModuleReady) return;
        if (this.isPrescriptionModuleVisible()) this.markModuleReady();
      }, MODULE_READY_POLL_MS);

      this.readyDeadline = setTimeout(() => {
        if (this.isModuleReady) return;
        this.blockPrescription();
      }, MODULE_READY_TIMEOUT_MS);
    },

    stopReadyWatch() {
      if (this.readyPoll) clearInterval(this.readyPoll);
      if (this.readyDeadline) clearTimeout(this.readyDeadline);
      this.readyPoll = null;
      this.readyDeadline = null;
    },

    markModuleReady() {
      this.isModuleReady = true;
      this.stopReadyWatch();
      this.watchModuleVisibility();
    },

    // O SDK nunca ficou utilizável. Do lado de cá não dá para ver o 401 — ele
    // acontece dentro de iframe cross-origin —, só que o módulo não abriu.
    //
    // A mensagem aqui é um PALPITE ("conta sem permissão" é a leitura mais
    // provável), e era um palpite que não deixava rastro. O `stage` distingue
    // as duas entradas: o deadline de 20s sem o container aparecer
    // (`module-ready`) e a rejeição imediata de um comando do SDK
    // (`module-command`).
    blockPrescription(error, stage = "module-ready") {
      this.stopReadyWatch();
      this.removeHubListeners();
      this.blockedMessage = memedMessageFor(error);
      this.isBlocked = true;
      this.errorCode = reportMemedFailure(stage, error, this.errorContext());
    },

    removeHubListeners() {
      const hub = this.hub;
      if (!hub || !hub.event || typeof hub.event.remove !== "function") return;
      try {
        if (this.onReadyHandler)
          hub.event.remove("core:onready", this.onReadyHandler);
        if (this.printedHandler)
          hub.event.remove("prescricaoImpressa", this.printedHandler);
      } catch (error) {
        // Versões antigas do SDK não expõem remove — a guarda isDestroyed
        // continua protegendo os handlers.
      }
    },

    showPrescriptionModule() {
      this.showAttempts += 1;

      // MdHub.command.send NÃO devolve sempre uma Promise spec-compliant:
      // quando a Memed recusa o token (401 dentro dos iframes dela — visível no
      // console como `POST .../v1/observability 401`), o send pode devolver
      // `undefined` ou um thenable legado (Zepto) cujo encadeamento não tem
      // `.catch`. Como este método é reinvocado por `core:onready` — FORA do
      // nosso try/catch —, encadear `.then().catch()` direto lançava um
      // TypeError síncrono que escapava como "Uncaught", sujava o Sentry e
      // deixava a tela morrer só no timeout de 20s. Proteger a chamada e
      // normalizar com Promise.resolve devolve o fluxo ao caminho já tratado:
      // recusa vira bloqueio ("Sua conta ainda não permite"), sem crash.
      let sent;
      try {
        sent = this.hub.command.send("plataforma.prescricao", "setPaciente", {
          nome: this.memedPatient.name,
          telefone: this.memedPatient.phone_number,
          cpf: this.memedPatient.cpf,
          data_nascimento: this.memedPatient.birthday
            ? formatFromENtoPT(this.memedPatient.birthday)
            : undefined,
          dificuldade_locomocao: true,
          idExterno: this.memedPatient.id
        });
      } catch (error) {
        this.blockPrescription(error, "module-command");
        return Promise.resolve();
      }

      return Promise.resolve(sent)
        .then(() => {
          this.hub.command.send("plataforma.prescricao", "setFeatureToggle", {
            deletePatient: false,
            removePatient: false,
            editPatient: false
          });
          this.hub.module.show("plataforma.prescricao");
          this.hub.module.updateStyle({
            moduleName: "plataforma.prescricao",
            styles: {
              position: "relative",
              zIndex: 1,
              width: "100%",
              height: "100%",
              overflowY: "auto"
            }
          });
          // A Memed carrega "Nova Fórmula Manipulada" como um módulo/iframe
          // à parte (plataforma.medicamento), irmão do iframe da prescrição
          // dentro do mesmo #iframe-container. Sem posicioná-lo, ele nasce em
          // fluxo normal e é empilhado ABAIXO do módulo de prescrição (que já
          // ocupa 100% da altura) em vez de sobrepor como modal — por isso o
          // usuário precisava rolar a página pra ver o formulário.
          //
          // Dentro desse módulo, o "x" de fechar (.panel .btn-close) fica
          // colado na ESQUERDA do .panel (o wrapper inteiro), enquanto o
          // cartão do formulário (.app-content) fica colado na DIREITA
          // (right:0; width:660px) — os dois são irmãos, não pai/filho. Se o
          // .panel ocupar 100% da largura do container, o "x" fica longe do
          // cartão. Por isso aqui o container fica estreito (do tamanho do
          // próprio cartão) e ancorado à direita, fazendo o "x" colar no
          // canto do cartão em vez de sobrar espaço à esquerda.
          this.hub.module.updateStyle({
            moduleName: "plataforma.medicamento",
            styles: {
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              width: "670px",
              maxWidth: "100%",
              height: "100%"
            }
          });

          this.$nextTick(() => {
            if (this.isPrescriptionModuleVisible()) this.markModuleReady();
          });
        })
        .catch(error => {
          // Rejeição do SDK é resposta definitiva: não adianta reenviar.
          this.blockPrescription(error, "module-command");
        });
    },

    isPrescriptionModuleVisible() {
      return isMemedModuleVisible();
    },

    // O botão "×" da Memed não fecha nada, só minimiza o módulo
    // (title="Clique para minimizar sua prescrição"), escondendo o iframe e
    // deixando a aba com o header vazio. Como esse botão vive dentro de um
    // iframe cross-origin (memed.com.br), não dá pra removê-lo ou
    // interceptar o clique diretamente — só observar o resultado: o
    // #iframe-container muda para display:none. Quando isso acontece,
    // fechamos a aba, como se o usuário tivesse clicado em "Fechar aba".
    watchModuleVisibility() {
      const container = document.getElementById("iframe-container");
      if (!container || this.moduleObserver) return;

      this.moduleObserver = new MutationObserver(() => {
        if (!this.isPrescriptionModuleVisible()) this.closeTab();
      });
      this.moduleObserver.observe(container, {
        attributes: true,
        attributeFilter: ["style"]
      });
    },

    closeTab() {
      window.close();
    }
  },
  computed: {
    ...mapGetters("user", ["getCurrentUser"]),
    patientName() {
      return this.memedPatient?.name || "";
    },
    hasFailed() {
      return !!this.loadingError || this.isBlocked;
    },
    failureMessage() {
      return this.loadingError || this.blockedMessage;
    },
    patientInitials() {
      return this.patientName
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0])
        .join("")
        .toUpperCase();
    }
  }
};
</script>

<style>
.rx-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #0b1220;
}

.rx-topbar {
  height: 56px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--space-5);
  background: #0b1220;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.rx-topbar__patient {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  color: #fff;
}

.rx-topbar__name {
  font-size: var(--font-size-sm);
  font-weight: var(--fw-semibold);
}

.rx-topbar__close {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.3);
}

.rx-content {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.rx-loader {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rx-loader--overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: #0b1220;
}

.rx-loader-msg {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  text-align: center;
}

.rx-loader-msg p {
  margin: 0;
  max-width: 320px;
  line-height: 1.5;
}

.rx-loader-title {
  font-size: 16px;
  font-weight: var(--fw-semibold);
  color: #fff;
}

.rx-loader-hint {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
}

.rx-loader-code {
  font-size: 12px;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.4);
  font-variant-numeric: tabular-nums;
}

.rx-loader-actions {
  display: flex;
  gap: var(--space-3);
  flex-wrap: wrap;
  justify-content: center;
}

.rx-body {
  /* !important necessário: o SDK Memed aplica um style="height:...px" inline
     nesta mesma div (data-container) ao inicializar, sobrescrevendo qualquer
     valor sem !important — mesmo workaround já documentado no CSS antigo de
     MeetingRoomView.vue (.memed-container-show), perdido na extração para
     esta view. Sem isso a div colapsa para a altura que o SDK define e o
     iframe some visualmente mesmo com o header renderizando normalmente. */
  flex: 1 !important;
  width: 100% !important;
  height: 100% !important;
  min-height: 0;
  display: flex;
}

.rx-body > * {
  width: 100% !important;
  height: 100% !important;
  overflow-y: auto !important;
}
</style>
